/**
 * « Coller mes notes existantes » — fonctions PURES du wizard de collage :
 * parseur du texte collé (Pronote, EcoleDirecte, tableur…), conversion
 * note → échelle de maîtrise 1..6, appariement lignes ↔ élèves TanoEd,
 * préparation du payload mass_update_scores.
 *
 * Tout est testable sans React. Le format de référence est le presse-papier
 * Pronote (TSV « NOM Prénom [⇥ Classe] ⇥ Note »), mais le parseur est
 * tolérant : avec ou sans ligne d'en-tête, colonne de notes détectée par
 * vote, virgules décimales, valeurs spéciales (Abs, Disp, N.Not, N.Rdu,
 * Inap, vide → non évalué, décision 2026-08-30), ligne « Moyenne » filtrée.
 */

import { parseCSVLine, fixEncoding, findClasseMatch } from './importElevesUtils';
import { classerAttendus } from './rechercheAttendus';

// ─── Normalisation des noms ─────────────────────────────────────────────

// Diacritiques combinants U+0300–U+036F (char codes : robuste aux éditeurs).
const DIACRITIQUES = new RegExp('[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']', 'g');

/** Clé de comparaison d'un nom : accents/casse/espaces neutralisés. */
export const normaliserNom = (s) => String(s ?? '')
    .normalize('NFD')
    .replace(DIACRITIQUES, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/** Identité affichée/appariée d'un élève TanoEd, priorité GAR (même
 *  recette que la synchronisation GAR du projet). */
export const identiteEleve = (e) => (e?.eleve_gar
    ? `${e.eleve_gar_nom || ''} ${e.eleve_gar_prenom || ''}`
    : `${e?.nom || ''} ${e?.prenom || ''}`).trim();

/**
 * Similarité 0..100 entre deux noms (Hamming positionnel rapporté à la
 * longueur max — recette SynchronisationGAR, seuil d'acceptation 80).
 */
export const similariteNoms = (a, b) => {
    const s1 = normaliserNom(a);
    const s2 = normaliserNom(b);
    const maxLen = Math.max(s1.length, s2.length);
    if (maxLen === 0) return 0;
    let distance = 0;
    for (let i = 0; i < maxLen; i += 1) {
        if (s1[i] !== s2[i]) distance += 1;
    }
    return Math.round(((maxLen - distance) / maxLen) * 100);
};

export const SEUIL_SIMILARITE = 80;

// ─── Valeurs spéciales (élève non évalué) ───────────────────────────────

/** Clé compacte d'une cellule : accents, points, espaces, casse neutralisés. */
const cleCellule = (s) => normaliserNom(s).replace(/[.\s]/g, '');

const SPECIALES = {
    abs: 'Abs', absent: 'Abs', absente: 'Abs',
    disp: 'Disp', dispense: 'Disp', dispensee: 'Disp',
    nnot: 'N.Not', nonnote: 'N.Not', nonnotee: 'N.Not',
    nrdu: 'N.Rdu', nonrendu: 'N.Rdu',
    inap: 'Inap', inapte: 'Inap',
};

/** 'Abs' | 'Disp' | … | 'vide' si la cellule est une valeur spéciale, sinon null. */
export const valeurSpeciale = (cellule) => {
    const cle = cleCellule(cellule);
    if (cle === '') return 'vide';
    return SPECIALES[cle] || null;
};

// ─── Degrés de maîtrise (mode décomposé) ────────────────────────────────

/** Libellés de maîtrise → score 1..6 : les libellés complets des logiciels
 *  de notes + les raccourcis d'affichage (TBM…) que certains établissements
 *  configurent. */
const MAITRISES = {
    tresbonnemaitrise: 6, 'a+': 6, tbm: 6,
    maitrisesatisfaisante: 5, a: 5, ms: 5,
    presquemaitrise: 4, b: 4, pm: 4,
    maitrisefragile: 3, c: 3, mf: 3,
    debutdemaitrise: 2, d: 2, dm: 2,
    maitriseinsuffisante: 1, e: 1, mi: 1,
};

/**
 * Score d'une cellule de maîtrise : 1..6 pour un libellé/raccourci/lettre
 * (ils SONT leur propre échelle), le chiffre BRUT pour une valeur numérique
 * (son échelle dépend de la politique d'établissement : sur 6 par défaut,
 * sur 4 au choix du prof — cf. convertirMaitrise), null si non évalué
 * (vide, Abs…), undefined si la cellule N'EST PAS une maîtrise (sert à la
 * détection de colonnes).
 */
export const scoreDepuisMaitrise = (cellule) => {
    if (valeurSpeciale(cellule) !== null) return null;
    const cle = cleCellule(cellule);
    if (/^[1-6]$/.test(cle)) return parseInt(cle, 10);
    return MAITRISES[cle];
};

/** Échelle sur 4 (MI, MF, MS, TBM — Début de maîtrise et Presque maîtrisé
 *  n'y existent pas) → échelle du suivi sur 6, via ses libellés réels. */
const ECHELLE_4_VERS_6 = { 1: 1, 2: 3, 3: 5, 4: 6 };

/**
 * Score FINAL d'une valeur de maîtrise selon sa colonne : les colonnes à
 * libellés sont déjà sur 6 ; une colonne à CHIFFRES suit l'échelle choisie
 * par le prof — '6' (défaut, échelle de l'appli) : littéral ; '4' :
 * conversion par libellés (1→1, 2→3, 3→5, 4→6).
 */
export const convertirMaitrise = (valeur, colonne, echelle = '6') => {
    if (valeur == null) return null;
    if (!colonne?.chiffres) return valeur;
    if (echelle === '4') return ECHELLE_4_VERS_6[Math.min(4, Math.max(1, valeur))];
    return Math.min(6, Math.max(1, valeur));
};

const parseNote = (cellule) => parseFloat(String(cellule).trim().replace(',', '.'));

// ─── Parseur ────────────────────────────────────────────────────────────

const nettoyerCellule = (c) => fixEncoding(String(c ?? '')
    .replace(/\r/g, '')
    .replace(/['"]/g, '')
    .trim());

/**
 * Séparateur du collage. Contrairement à detectSeparator (générique), la
 * virgule n'est retenue que si elle survit au retrait des DÉCIMALES
 * françaises (« 12,5 ») — sinon un collage « NOM Prénom 12,5 » sans
 * tabulation se ferait découper au milieu de la note.
 */
const choisirSeparateur = (ligne) => {
    if (ligne.includes('\t')) return '\t';
    if (ligne.includes(';')) return ';';
    if (ligne.replace(/\d,\d/g, '').includes(',')) return ',';
    return null;
};

/** CONTRAT DE STRUCTURE (pas d'IA, pas de devinette) : la 1re ligne doit
 *  être l'en-tête du tableau, première colonne « Élèves »/« Nom », c'est
 *  ce que collent Pronote et consorts. */
const estEnTete = (cellules) => {
    const cle = cleCellule(cellules?.[0] || '');
    return cle === 'eleves' || cle === 'eleve' || cle === 'nom' || cle === 'noms';
};

const ERREUR_EN_TETE = 'Le tableau doit commencer par sa ligne d\'en-tête (première colonne « Élèves »). Dans votre logiciel, copiez le tableau AVEC ses titres de colonnes.';

/**
 * Parse le texte collé.
 * @returns {{
 *   lignes: [{ ligneNo, nomBrut, classeBrute, noteBrute, note, special }],
 *   ignorees: [{ ligneNo, contenu, raison }],
 *   erreur: string|null,   // seulement si RIEN d'exploitable
 * }}
 * `note` : number|null ; `special` : 'Abs'|'Disp'|'N.Not'|'N.Rdu'|'Inap'|'vide'|null.
 */
export const parserNotesCollees = (texte) => {
    const vide = { lignes: [], ignorees: [], colonnesMaitrise: [], erreur: null };
    if (typeof texte !== 'string' || !texte.trim()) {
        return { ...vide, erreur: 'Le collage est vide.' };
    }

    // 1. Découpe + parsing cellule à cellule
    const brutes = texte.split('\n');
    const premiere = brutes.find((l) => l.trim().length > 0) || '';
    const separateur = choisirSeparateur(premiere);
    const rangees = [];
    brutes.forEach((brute, idx) => {
        if (!brute.trim()) return;
        const cellules = separateur
            ? parseCSVLine(brute, separateur).map(nettoyerCellule)
            : [nettoyerCellule(brute)];
        if (cellules.some((c) => c !== '')) rangees.push({ ligneNo: idx + 1, cellules });
    });
    if (!rangees.length) return { ...vide, erreur: 'Le collage est vide.' };

    // 2. EN-TÊTE OBLIGATOIRE : c'est lui qui nomme les colonnes Notes et
    //    Classe — aucune détection par devinette.
    if (!estEnTete(rangees[0].cellules)) {
        return { ...vide, erreur: ERREUR_EN_TETE };
    }
    let idxNote = null;
    let idxClasse = null;
    rangees[0].cellules.forEach((c, i) => {
        const cle = cleCellule(c);
        if (idxNote === null && cle.includes('note')) idxNote = i;
        if (idxClasse === null && (cle === 'classe' || cle === 'classes')) idxClasse = i;
    });
    const donnees = rangees.slice(1);
    if (!donnees.length) {
        return { ...vide, erreur: 'Le collage ne contient que la ligne d\'en-tête.' };
    }

    const largeur = Math.max(...donnees.map((r) => r.cellules.length));

    // 3. Colonnes de MAÎTRISE (mode décomposé) : toute colonne restante
    //    dont la majorité des cellules non vides est un degré de maîtrise
    //    (« Très bonne maîtrise », A+..E, chiffre 1-6). Ordre de gauche à
    //    droite = ordre du matching avec les cases attendu × compétence.
    const entetes = rangees[0].cellules;
    const colonnesMaitrise = [];
    for (let col = 1; col < largeur; col += 1) {
        if (col === idxNote || col === idxClasse) continue;
        let reconnues = 0;
        let nonVides = 0;
        let enChiffres = 0;
        let maxObserve = 0;
        donnees.forEach((r) => {
            const c = String(r.cellules[col] ?? '');
            if (c.trim() === '') return;
            nonVides += 1;
            const s = scoreDepuisMaitrise(c);
            if (s !== undefined) reconnues += 1;
            if (s != null && /^[0-9]+$/.test(cleCellule(c))) {
                enChiffres += 1;
                maxObserve = Math.max(maxObserve, s);
            }
        });
        if (nonVides > 0 && reconnues >= nonVides / 2) {
            // `chiffres` : l'échelle de la colonne devra être déclarée par le
            // prof (sur 6 par défaut, sur 4 possible) — un chiffre seul ne
            // porte pas son échelle, contrairement à un libellé.
            colonnesMaitrise.push({
                index: col,
                entete: entetes[col] || `Colonne ${col + 1}`,
                chiffres: enChiffres > 0 && enChiffres >= reconnues / 2,
                maxObserve,
            });
        }
    }

    if (idxNote === null && colonnesMaitrise.length === 0) {
        return {
            ...vide,
            erreur: 'Aucune colonne « Notes » dans l\'en-tête, ni colonne de maîtrises. Copiez le tableau avec sa colonne de notes (ou de compétences).',
        };
    }

    // 5. Lignes de données
    const lignes = [];
    const ignorees = [];
    donnees.forEach(({ ligneNo, cellules }) => {
        const nomBrut = cellules[0] || '';
        if (!nomBrut) {
            ignorees.push({ ligneNo, contenu: cellules.join(' | '), raison: 'ligne sans nom' });
            return;
        }
        // Vraisemblance : un nom d'élève ne contient pas de chiffres — filtre
        // les collages accidentels (« Rendez-vous salle 12 »).
        if (/[0-9]/.test(nomBrut)) {
            ignorees.push({ ligneNo, contenu: cellules.join(' | '), raison: `nom illisible « ${nomBrut} »` });
            return;
        }
        // Ligne récapitulative Pronote (« Moyenne de la classe »…) : silencieuse.
        if (normaliserNom(nomBrut).startsWith('moyenne')) return;

        // Maîtrises décomposées de la ligne (alignées sur colonnesMaitrise) —
        // une cellule non reconnue vaut « non évalué ».
        const maitrises = colonnesMaitrise.map(({ index }) => {
            const s = scoreDepuisMaitrise(cellules[index] ?? '');
            return s === undefined ? null : s;
        });

        const base = {
            ligneNo, nomBrut,
            classeBrute: idxClasse !== null ? (cellules[idxClasse] || null) : null,
            maitrises,
        };

        if (idxNote === null) {
            lignes.push({ ...base, noteBrute: '', note: null, special: 'vide' });
            return;
        }
        const noteBrute = cellules[idxNote] ?? '';
        const special = valeurSpeciale(noteBrute);
        if (special !== null) {
            lignes.push({ ...base, noteBrute, note: null, special });
            return;
        }
        const note = parseNote(noteBrute);
        if (Number.isNaN(note)) {
            // En mode décomposé la ligne reste exploitable (ses maîtrises).
            if (colonnesMaitrise.length > 0) {
                lignes.push({ ...base, noteBrute, note: null, special: 'vide' });
                return;
            }
            ignorees.push({ ligneNo, contenu: `${nomBrut} | ${noteBrute}`, raison: `note illisible « ${noteBrute} »` });
            return;
        }
        lignes.push({ ...base, noteBrute, note, special: null });
    });

    // Dernier filet : un tableau à en-tête valide mais sans aucune ligne
    // élève exploitable (ou presque) n'est pas une liste de notes.
    if (!lignes.length) {
        return { ...vide, erreur: 'Aucune ligne « élève + note » reconnue sous l\'en-tête. Copiez le tableau complet depuis votre logiciel ou votre tableur.' };
    }
    if (ignorees.length > lignes.length) {
        return { ...vide, erreur: 'Ce contenu ne ressemble pas à une liste d\'élèves avec leurs notes. Copiez le tableau des notes, puis recollez.' };
    }
    return { lignes, ignorees, colonnesMaitrise, erreur: null };
};

// ─── Conversion note → échelle de maîtrise ──────────────────────────────

/**
 * note/barème → entier 1..6 (échelle TBM..MI du suivi). Décisions actées :
 * toute copie rendue vaut au moins 1 (le 0 est réservé aux non-évalués),
 * une note > barème est plafonnée à 6 (alerte affichée côté UI).
 */
export const noteVersScore = (note, bareme) => {
    if (note == null || !(bareme > 0)) return null;
    return Math.min(6, Math.max(1, Math.round((note / bareme) * 6)));
};

// ─── Appariement lignes ↔ élèves TanoEd ─────────────────────────────────

/**
 * Appariement automatique. Chaque ligne finit dans UNE des sorties :
 *   - apparies  : [{ ligne, eleve, similarite|null }]
 *   - aResoudre : [{ ligne, raison: 'inconnu'|'homonymes'|'doublon',
 *                    candidats: [eleve…], ligneEnConflit|null }]
 * plus `elevesSansNote` : élèves TanoEd qu'aucune ligne ne couvre.
 * Rien n'est bloquant : l'arbitrage manuel (étape 2) vide `aResoudre`.
 *
 * @param {Array} lignes   sortie du parseur
 * @param {Array} eleves   classeDetail.eleves (ou groupe)
 * @param {Object} opts    { estGroupe, classesDuGroupe: [{id, nom}],
 *                           classeCourante: nom de la classe (vue classe) }
 */
export const apparierEleves = (lignes, eleves, { estGroupe = false, classesDuGroupe = [], classeCourante = null } = {}) => {
    const infos = (eleves || []).map((eleve) => ({
        eleve,
        cle: normaliserNom(identiteEleve(eleve)),
        classeCle: normaliserNom(eleve?.classe_nom || ''),
    }));
    const pris = new Map(); // eleve.id → ligneNo
    const apparies = [];
    const aResoudre = [];

    const libres = () => infos.filter(({ eleve }) => !pris.has(eleve.id));

    (lignes || []).forEach((ligne) => {
        const cleLigne = normaliserNom(ligne.nomBrut);

        // GARDE-FOU vue classe : une ligne qui porte une AUTRE classe que la
        // classe courante (collage d'un groupe de besoins) n'est JAMAIS
        // appariée automatiquement — même un homonyme parfait irait au
        // mauvais élève. Elle part en arbitrage avec sa vraie raison.
        if (!estGroupe && classeCourante && ligne.classeBrute) {
            const memeClasse = findClasseMatch(ligne.classeBrute, [{ id: 0, nom: classeCourante }]);
            if (!memeClasse) {
                aResoudre.push({
                    ligne, raison: 'autreClasse',
                    candidats: libres().map(({ eleve }) => eleve),
                    ligneEnConflit: null,
                });
                return;
            }
        }

        // Résolution de la classe collée (groupes de besoins multi-classes,
        // troncatures gérées par findClasseMatch) — non bloquante.
        const classeResolue = estGroupe && ligne.classeBrute
            ? findClasseMatch(ligne.classeBrute, classesDuGroupe)
            : null;
        const cleClasse = classeResolue ? normaliserNom(classeResolue.nom) : null;

        // 1. Égalité stricte du nom normalisé
        let exacts = infos.filter((i) => i.cle === cleLigne);
        if (exacts.length > 1 && cleClasse) {
            const filtres = exacts.filter((i) => i.classeCle === cleClasse);
            if (filtres.length) exacts = filtres;
        }
        const exactsLibres = exacts.filter(({ eleve }) => !pris.has(eleve.id));

        if (exactsLibres.length === 1) {
            const { eleve } = exactsLibres[0];
            pris.set(eleve.id, ligne.ligneNo);
            apparies.push({ ligne, eleve, similarite: null });
            return;
        }
        if (exactsLibres.length > 1) {
            aResoudre.push({
                ligne, raison: 'homonymes',
                candidats: exactsLibres.map(({ eleve }) => eleve),
                ligneEnConflit: null,
            });
            return;
        }
        if (exacts.length > 0) {
            // Même nom déjà consommé par une ligne précédente (doublon de collage)
            aResoudre.push({
                ligne, raison: 'doublon',
                candidats: libres().map(({ eleve }) => eleve),
                ligneEnConflit: pris.get(exacts[0].eleve.id) ?? null,
            });
            return;
        }

        // 2. Repli similarité parmi les élèves encore libres : meilleur ≥ 80
        //    ET seul candidat ≥ 80 (recette synchro GAR).
        const scores = libres()
            .map((i) => ({ ...i, score: similariteNoms(ligne.nomBrut, identiteEleve(i.eleve)) }))
            .sort((a, b) => b.score - a.score);
        const auDessus = scores.filter((s) => s.score >= SEUIL_SIMILARITE);
        if (auDessus.length === 1) {
            const { eleve, score } = auDessus[0];
            pris.set(eleve.id, ligne.ligneNo);
            apparies.push({ ligne, eleve, similarite: score });
            return;
        }

        // 3. Inconnu (ou similarité ambiguë) → arbitrage manuel, candidats
        //    pré-classés par ressemblance décroissante.
        aResoudre.push({
            ligne, raison: 'inconnu',
            candidats: scores.map(({ eleve }) => eleve),
            ligneEnConflit: null,
        });
    });

    const elevesSansNote = (eleves || []).filter((e) => !pris.has(e.id));
    return { apparies, aResoudre, elevesSansNote };
};

// ─── Payload des scores ─────────────────────────────────────────────────

/**
 * Construit le tableau `[{esfId, score}]` pour mass_update_scores à partir
 * des appariements FINAUX (auto + arbitrages) et du détail d'évaluation
 * relu après création (`eleves_resultats`). Un élève apparié à note réelle
 * reçoit son score sur TOUS ses esfIds (un par attendu × compétence —
 * décision : même note recopiée). Les non-appariés et notes spéciales ne
 * sont PAS envoyés (les ESF naissent à null = non évalué).
 */
export const construirePayloadScores = (apparies, elevesResultats, bareme) => {
    const parEleve = new Map();
    (elevesResultats || []).forEach((er) => {
        parEleve.set(er.id, (er.resultats || []).map((r) => r.esfId).filter((x) => x != null));
    });
    const payload = [];
    (apparies || []).forEach(({ ligne, eleve }) => {
        const score = noteVersScore(ligne.note, bareme);
        if (score == null) return;
        (parEleve.get(eleve.id) || []).forEach((esfId) => {
            payload.push({ esfId, score });
        });
    });
    return payload;
};

/**
 * Ordre canonique des cases d'une éval collée : attendu par attendu, chaque
 * attendu déroulant SES compétences cochées (`attendu.compIds`). Sert au
 * mode NOTE SEULE (recopie sur chaque case).
 */
export const casesEvaluees = (attendus) =>
    (attendus || []).flatMap((a) => (a.compIds || []).map((compId) => ({ attendu: a, compId })));

// ─── Mappage explicite du mode décomposé (1 colonne collée = 1 ligne) ───

/** Aplat des attendus d'un niveau depuis la réponse de useTheme. La
 *  rubrique (objectif / automatisme) suit : le menu la montre en couleur ; le
 *  niveau aussi : un attendu choisi dans un autre niveau le garde. */
export const attendusDuNiveau = (themes) => (themes || []).flatMap((t) =>
    (t.descripteur || []).flatMap((d) => (d.attendus || []).map((a) => ({
        id: a.id,
        description: a.description,
        rubrique: a.rubrique,
        niveau: a.niveau,
        themeNom: t.nom,
        descripteurNom: d.nom,
    }))));

/**
 * Les cases de l'évaluation (mode décomposé), une par ligne qui a un
 * attendu, dans l'ordre des colonnes, chacune avec l'index de SA colonne.
 * L'attendu d'une ligne est celui qu'elle garde (`m.attendu`, choisi dans le
 * menu, éventuellement dans un AUTRE niveau, 2026-09-29) ; à défaut (ligne
 * proposée automatiquement), celui de la liste du niveau de la classe. Avant,
 * seule la liste de la classe servait : un attendu d'un autre niveau était
 * créé dans l'évaluation mais ses notes se perdaient.
 */
export const casesDepuisMappages = (mappages, attendusNiveau) => {
    const parId = new Map((attendusNiveau || []).map((a) => [a.id, a]));
    return (mappages || [])
        .filter((m) => m.attenduId != null)
        .map((m) => ({ attendu: m.attendu || parId.get(m.attenduId), compId: m.compId, colIndex: m.colIndex }))
        .filter((c) => c.attendu);
};

export const SEUIL_PROPOSITION = 0.6;
const ECART_PROPOSITION = 0.2;

/**
 * Pré-mappage : pour chaque colonne collée, l'attendu que la recherche par
 * mots classe NETTEMENT en tête. Depuis le 2026-09-29, c'est le classement
 * du menu (classerAttendus : mots courants du programme ignorés, mots rares
 * pondérés, correspondances ; « Calculer une somme de nombres décimaux » ne
 * propose plus l'automatisme 1/10 = 0,1 sur le seul mot « décimaux »).
 * Proposé SEULEMENT si sa couverture ≥ 0,6 ET qu'il devance le 2e d'au moins
 * 0,2 ; sinon null (choix manuel). Jamais silencieux : l'appelant pose un
 * badge « proposition ».
 */
export const proposerAttendus = (colonnesMaitrise, attendus) => {
    const liste = (attendus || []).map((a) => ({
        id: a.id,
        description: a.description,
        rubrique: a.rubrique,
        theme: a.themeNom,
        descripteur: a.descripteurNom,
    }));
    return (colonnesMaitrise || []).map((col) => {
        const [meilleur, second] = classerAttendus(liste, col?.entete, { max: 2 });
        if (!meilleur || meilleur.couverture < SEUIL_PROPOSITION) {
            return { attenduId: null, score: meilleur?.couverture || 0 };
        }
        if (second && meilleur.couverture - second.couverture < ECART_PROPOSITION) {
            return { attenduId: null, score: meilleur.couverture };
        }
        return { attenduId: meilleur.id, score: meilleur.couverture };
    });
};

/** Colonnes en DOUBLON exact (même attendu · même compétence sur ≥ 2
 *  lignes) → Set des colIndex fautifs. Lignes incomplètes ignorées. */
export const doublonsMappages = (mappages) => {
    const parCle = new Map();
    (mappages || []).forEach((m) => {
        if (m.attenduId == null || m.compId == null) return;
        const cle = `${m.attenduId}:${m.compId}`;
        if (!parCle.has(cle)) parCle.set(cle, []);
        parCle.get(cle).push(m.colIndex);
    });
    const fautifs = new Set();
    parCle.forEach((cols) => {
        if (cols.length > 1) cols.forEach((c) => fautifs.add(c));
    });
    return fautifs;
};

/** Tableau `exercices` du POST depuis les mappages : un exercice par
 *  attendu distinct (ordre de première apparition), comp_ids = union des
 *  compétences de ses lignes. */
export const exercicesDepuisMappages = (mappages) => {
    const parAttendu = new Map();
    (mappages || []).forEach((m) => {
        if (m.attenduId == null) return;
        if (!parAttendu.has(m.attenduId)) parAttendu.set(m.attenduId, []);
        const comps = parAttendu.get(m.attenduId);
        if (!comps.includes(m.compId)) comps.push(m.compId);
    });
    return [...parAttendu.entries()].map(([attenduId, compIds]) => ({
        comp_ids: compIds,
        attendu_id: attenduId,
    }));
};

/** En-têtes des colonnes collées laissées SANS attendu : elles ne sont pas
 *  importées (2026-09-29, une colonne générale comme « Communiquer sa
 *  réponse » n'a pas d'attendu). Dans l'ordre du collage. */
export const colonnesNonImportees = (mappages, colonnesMaitrise = []) => (mappages || [])
    .filter((m) => m.attenduId == null)
    .map((m) => colonnesMaitrise[m.colIndex]?.entete ?? '');

/**
 * Payload mass_update_scores du mode DÉCOMPOSÉ : chaque case lit SA colonne
 * collée (`colIndex`, posé par le mappage) ; sans `colIndex`, la k-ième case
 * lit la k-ième colonne. Depuis qu'une colonne peut rester sans attendu (non
 * importée), les cases ne suivent plus forcément les colonnes une à une : sans
 * `colIndex`, les notes glisseraient d'une colonne. La résolution passe par
 * (attenduId, competenceId) relus dans eleves_resultats — jamais par l'ordre
 * du serializer. `colonnesMaitrise`/`echelles` : conversion des colonnes en
 * chiffres selon l'échelle déclarée (convertirMaitrise).
 */
export const construirePayloadScoresDecompose = (apparies, elevesResultats, cases, colonnesMaitrise = [], echelles = []) => {
    const parEleve = new Map();
    (elevesResultats || []).forEach((er) => {
        const parCase = new Map();
        (er.resultats || []).forEach((r) => {
            if (r.esfId != null && r.attenduId != null && r.competenceId != null) {
                parCase.set(`${r.attenduId}:${r.competenceId}`, r.esfId);
            }
        });
        parEleve.set(er.id, parCase);
    });
    const payload = [];
    (apparies || []).forEach(({ ligne, eleve }) => {
        const parCase = parEleve.get(eleve.id);
        if (!parCase) return;
        (cases || []).forEach(({ attendu, compId, colIndex }, k) => {
            const col = colIndex ?? k;
            const score = convertirMaitrise(
                (ligne.maitrises || [])[col],
                colonnesMaitrise[col],
                echelles[col]
            );
            if (score == null) return;
            const esfId = parCase.get(`${attendu.id}:${compId}`);
            if (esfId != null) payload.push({ esfId, score });
        });
    });
    return payload;
};
